<template>
    <div>
        <q-infinite-scroll @load="onLoad" :offset="250" scroll-target="window">
            <template v-for="(group, dateIndex) in items" :key="group.date">
                <div class="row q-col-gutter-md">
                    <div class="col-12">
                        <h3>
                            {{ group.formattedDate }}
                            <q-badge
                                v-for="hint in group.hints"
                                :key="hint.name"
                                color="primary"
                                class="text-h6 q-ml-sm"
                            >
                                &nbsp;<q-icon name="mail"></q-icon
                                >{{ hint.name }}&nbsp;
                            </q-badge>
                        </h3>
                    </div>
                </div>

                <!-- Case 1: Overview (grouped by campaign) -->
                <template v-if="fetchOverview">
                    <div v-if="group.campaigns && group.campaigns.length > 0">
                        <div
                            v-for="campaignGroup in group.campaigns"
                            :key="campaignGroup.id"
                            class="row q-col-gutter-md q-mb-lg"
                        >
                            <div class="col-12">
                                <p>
                                    <strong>{{ campaignGroup.titleLong }}</strong>
                                </p>
                                <div
                                    class="row q-col-gutter-md guestCampaignShifts"
                                >
                                    <div
                                        v-for="shift in campaignGroup.shifts"
                                        :key="shift.id"
                                        class="col col-xs-12 col-sm-6 col-md-4 col-lg-3"
                                    >
                                        <shift-block
                                            :shift="shift"
                                            :user="user"
                                            :csrf-token="csrfToken"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="q-mb-lg text-grey">
                        <p>{{ getTranslation("notification.system.no_shifts_found", "Keine Schichten gefunden") }}</p>
                    </div>
                </template>

                <!-- Case 2: Single Campaign -->
                <template v-else>
                    <div v-if="group.shifts && group.shifts.length > 0" class="row q-col-gutter-md guestCampaignShifts q-mb-lg">
                        <div
                            v-for="shift in group.shifts"
                            :key="shift.id"
                            class="col col-xs-12 col-sm-6 col-md-4 col-lg-3"
                        >
                            <shift-block
                                :shift="shift"
                                :user="user"
                                :csrf-token="csrfToken"
                                class="full-height"
                            />
                        </div>
                    </div>
                    <div v-else class="q-mb-lg text-grey">
                        <p>{{ getTranslation("notification.system.no_shifts_found", "Keine Schichten gefunden") }}</p>
                    </div>
                </template>
            </template>

            <template v-slot:loading>
                <div class="row justify-center q-my-md">
                    <q-spinner-dots color="primary" size="40px" />
                </div>
            </template>
        </q-infinite-scroll>

        <div
            v-if="items.length === 0 && !loadingInitial"
            class="text-center q-pa-md"
        >
            <p>
                {{
                    getTranslation(
                        "notification.system.no_shifts_found",
                        "Keine Schichten gefunden",
                    )
                }}
            </p>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from "vue";
import axios from "axios";
import ShiftBlock from "./ShiftBlock.vue";

export default {
    components: {
        ShiftBlock,
    },
    props: {
        campaignId: {
            type: [Number, String],
            default: null,
        },
        fetchOverview: {
            type: Boolean,
            default: false,
        },
        user: {
            type: Object,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const items = ref([]);
        const page = ref(1);
        const loadingInitial = ref(true);

        onMounted(() => {
            console.log("ShiftInfiniteScroll mounted", {
                campaignId: props.campaignId,
                fetchOverview: props.fetchOverview,
                items: items.value.length,
            });
            // Force initial load after component is ready
            setTimeout(() => {
                if (items.value.length === 0) {
                    console.log("ShiftInfiniteScroll forcing initial load");
                    onLoad(1, (stop) => {
                        console.log(
                            "ShiftInfiniteScroll initial load done, stop:",
                            stop,
                        );
                    });
                }
            }, 500);
        });

        const onLoad = (index, done) => {
            if (loadingInitial.value && index === 1) {
                console.log("ShiftInfiniteScroll first onLoad triggered");
            }
            console.log("ShiftInfiniteScroll onLoad called", {
                index,
                page: page.value,
                loadingInitial: loadingInitial.value,
            });
            let url = "";
            if (props.fetchOverview) {
                url = "/api/shifts/overview";
            } else if (props.campaignId) {
                url = `/api/shifts/campaign/${props.campaignId}`;
            } else {
                done(true); // Stop if nothing to fetch
                return;
            }

            axios
                .get(url, {
                    params: {
                        page: page.value,
                    },
                })
                .then((response) => {
                    const newData = response.data.data;
                    const hasMore = response.data.hasMore;

                    if (newData.length > 0) {
                        items.value.push(...newData);
                        page.value++;
                        done(!hasMore);
                    } else {
                        if (hasMore) {
                            // If no items in this page but there's more in future, keep fetching
                            page.value++;
                            onLoad(index, done);
                        } else {
                            done(true); // No more data at all
                        }
                    }
                    loadingInitial.value = false;
                })
                .catch((error) => {
                    console.error("Error loading shifts:", error);
                    done(true); // Stop on error
                    loadingInitial.value = false;
                });
        };

        const getTranslation = (key, fallback) => {
            return (
                (window.translations && window.translations[key]) || fallback
            );
        };

        return {
            items,
            onLoad,
            loadingInitial,
            getTranslation,
        };
    },
};
</script>
